import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	AGENT_STATE_VARIANT,
	agentStateDotClass,
	agentStateLabel,
	dbTypeLabel,
	engineChangeText,
	fmtDateTime,
	fmtDayShort,
	STATUS_FILL,
	STATUS_LABEL,
	STATUS_VARIANT,
} from "./format";
import type { AgentState, PharmacyTrendPoint, SyncStatus } from "./types";

/**
 * Kropka stanu usługi sync (lista, kolumna „Agent”) — te same rozróżnienia co
 * `AgentStateChip`, ale bez etykiety; stan jest w `title`/`aria-label`.
 */
export function AgentStateDot({
	state,
	className,
}: {
	state: AgentState | null | undefined;
	className?: string;
}) {
	const label = `Usługa sync: ${!state || state === "none" ? "brak agenta" : agentStateLabel(state)}`;
	return (
		<span
			role="img"
			aria-label={label}
			title={label}
			className={cn(
				"inline-block size-2 shrink-0 rounded-full",
				agentStateDotClass(state),
				className,
			)}
		/>
	);
}

export function StatusChip({
	status,
	className,
}: {
	status: SyncStatus | null;
	className?: string;
}) {
	if (!status) return <span className="text-xs text-muted-foreground">—</span>;
	const variant = STATUS_VARIANT[status];
	return (
		<span
			className={cn(
				badgeBase,
				variant ? statusClasses[variant] : "border-border bg-muted text-muted-foreground",
				className,
			)}
		>
			{STATUS_LABEL[status]}
		</span>
	);
}

/** Strona APTEKA (ERP) bez stanowiska sync — porównanie tylko Source → Target. */
export function NoAgentChip({ className }: { className?: string }) {
	return (
		<span
			className={cn(badgeBase, "border-border bg-muted text-muted-foreground", className)}
			title="Apteka nie ma włączonego stanowiska synchronizacji — porównanie tylko Source → Target"
		>
			brak agenta
		</span>
	);
}

/** Stan usługi sync (`agentState` listy / `serviceState` bindingu). `none` = brak agenta. */
export function AgentStateChip({
	state,
	title,
	className,
}: {
	state: AgentState | null | undefined;
	title?: string;
	className?: string;
}) {
	if (!state || state === "none") return <NoAgentChip className={className} />;
	const variant = AGENT_STATE_VARIANT[state];
	return (
		<span
			className={cn(
				badgeBase,
				variant ? statusClasses[variant] : "border-border bg-muted text-muted-foreground",
				className,
			)}
			title={title}
		>
			{agentStateLabel(state)}
		</span>
	);
}

/**
 * Odznaka „zmiana silnika FB→ORA <data>” (D42) — lista (kolumna silnika) i karta
 * (konektor). Po zmianie rozjazd A↔S wskazuje raczej na Source (stare roboty).
 */
export function EngineChangeBadge({
	change,
	className,
}: {
	change: {
		engineChangedAt?: string | null;
		engineChangedFrom?: string | null;
		engineChangedTo?: string | null;
	};
	className?: string;
}) {
	const text = engineChangeText(change);
	if (!text) return null;
	return (
		<span
			className={cn(badgeBase, "px-1.5 py-0", statusClasses.warning, className)}
			title={`Stanowisko potwierdziło silnik ${dbTypeLabel(change.engineChangedTo)} (wcześniej ${dbTypeLabel(change.engineChangedFrom)}) — ${fmtDateTime(change.engineChangedAt)}. Rozjazd Apteka ↔ Source po tej dacie = podejrzany Source (stare roboty mogą pytać starym dialektem).`}
		>
			{text}
		</span>
	);
}

/** Odznaka „wstrzymana” (D47) — karta apteki (nagłówek, ustawienia) i lista. */
export function PausedBadge({ className }: { className?: string }) {
	return (
		<span
			className={cn(badgeBase, statusClasses.warning, className)}
			title="Synchronizacja wstrzymana: usługa nie pobiera jobów (heartbeat działa dalej)"
		>
			wstrzymana
		</span>
	);
}

/** Mini-pasek statusów (najstarszy pierwszy), najgorszy status dnia po tabelach. */
export function TrendBar({ points }: { points: PharmacyTrendPoint[] }) {
	if (points.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
	const label = points
		.map((p) => `${fmtDayShort(p.date)} ${p.status ? STATUS_LABEL[p.status] : "—"}`)
		.join(", ");
	return (
		<span
			role="img"
			aria-label={`Trend 14 dni: ${label}`}
			title={label}
			className="inline-flex h-3.5 items-end gap-px"
		>
			{points.map((p) => (
				<i
					key={p.date}
					className={cn(
						"block w-[3px] rounded-[1px]",
						p.status ? STATUS_FILL[p.status] : "bg-border",
						p.status === "mismatch" || p.status === "warn" ? "h-3.5" : "h-2 opacity-50",
					)}
				/>
			))}
		</span>
	);
}
